import React from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import { Card, CardContent, Chip, MenuItem, Tooltip, Typography } from "@material-ui/core";
import DoneIcon from "@material-ui/icons/Done";
import CloseIcon from "@material-ui/icons/Close";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import { IMAGES_ENDPOINT } from "@icarius-connection/endpoints";
import DotsMenu from "@icarius-common/dotsMenu";
import { MedalTableIcon } from "@icarius-icons/index";
import { useSelector } from "react-redux";
import { getIsAdmin } from "@icarius-app/selectors";
import ProgressBar from "./progressBar";
import Thumbnail from "./thumbnail";

const TrainingSummary = (props) => {
    const { isActive, data, handleOpen, handleDownloadCertificates, handleShowParticipants } = props;

    const isAdmin = useSelector(getIsAdmin);

    return (
        <Card style={{ width: "100%", borderRadius: 15, cursor: "pointer" }} onClick={handleOpen}>
            <Thumbnail path={data.preview ? IMAGES_ENDPOINT + data.preview.path : ""} isVideo={data.preview?.isVideo} />
            <CardContent style={{ paddingBottom: 15 }}>
                <Tooltip
                    title={
                        <>
                            <b>{data.name}:</b> {data.description}
                            <br />
                            <b>{getLocalizedString("themes")}:</b> {data.themes.join(", ")}
                        </>
                    }
                    arrow
                >
                    <div style={{ marginBottom: 15 }}>
                        <div
                            style={{
                                marginBottom: 15,
                                display: "flex",
                                alignItems: "center",
                                position: "relative",
                            }}
                        >
                            <Typography
                                className="whiteText"
                                align="center"
                                style={{
                                    padding: "0px 20px",
                                    fontSize: 18,
                                    lineHeight: "30px",
                                    overflow: "hidden",
                                    display: "-webkit-box" /* Required for the clamping mechanism */,
                                    WebkitBoxOrient: "vertical" /* Required for the clamping mechanism */,
                                    WebkitLineClamp: 1 /* The number of lines to show */,
                                    lineClamp: 1 /* Standard property for future support */,
                                    width: "100%",
                                }}
                            >
                                {data.name}
                            </Typography>
                            {(data.isInstructor || isAdmin || (data.hasCertificates && data.isParticipant)) && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 2,
                                        right: -5,
                                    }}
                                >
                                    <DotsMenu noPadding>
                                        {(data.isInstructor || isAdmin) && (
                                            <MenuItem onClick={handleShowParticipants}>
                                                {getLocalizedString("viewParticipants")}
                                            </MenuItem>
                                        )}
                                        {data.hasCertificates && data.isParticipant && (
                                            <MenuItem onClick={handleDownloadCertificates}>
                                                {getLocalizedString("downloadCertificates")}
                                            </MenuItem>
                                        )}
                                    </DotsMenu>
                                </div>
                            )}
                        </div>
                        <div
                            style={{
                                padding: 10,
                                background: "var(--tertiaryBackgroundColor)",
                                border: "1px solid var(--borderColor)",
                                borderRadius: 15,
                                display: "flex",
                                flexDirection: "column",
                                gap: 14,
                            }}
                        >
                            <div
                                style={{
                                    height: 50,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography className="whiteText" style={{ fontSize: 14 }}>
                                    {getLocalizedString("instructor")}:
                                </Typography>
                                <Typography className="whiteText" style={{ fontWeight: 500 }}>
                                    {data.instructor}
                                </Typography>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography className="whiteText" style={{ fontSize: 14 }}>
                                    {getLocalizedString("length")}:
                                </Typography>
                                <Typography className="whiteText" style={{ fontWeight: 500 }}>
                                    {data.length} {getLocalizedString("hrs")}
                                </Typography>
                            </div>
                            <Typography className="whiteText" align="center" style={{ fontSize: 14 }}>
                                {`${data.from} - ${data.to}`}
                            </Typography>
                        </div>
                    </div>
                </Tooltip>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                    }}
                >
                    {data.isInstructor && (
                        <Chip
                            size="small"
                            className="whiteText"
                            style={{
                                height: 30,
                                width: "100%",
                                background: "var(--ownBlue)",
                                cursor: "pointer",
                                flexShrink: 0,
                            }}
                            label={getLocalizedString("instructor")}
                            icon={<SupervisorAccountIcon style={{ fill: "var(--mainText)" }} />}
                        />
                    )}
                    {!isActive && data.isParticipant && (
                        <>
                            <Chip
                                size="small"
                                style={{
                                    height: 30,
                                    width: "100%",
                                    background: data.isAssistanceCompleted ? "var(--ownGreen)" : "var(--ownRed)",
                                    color: "white",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                }}
                                label={
                                    data.isAssistanceCompleted
                                        ? getLocalizedString("assistanceCompleted")
                                        : getLocalizedString("assistanceIncompleted")
                                }
                                icon={
                                    data.isAssistanceCompleted ? (
                                        <DoneIcon style={{ fill: "#fff" }} />
                                    ) : (
                                        <CloseIcon style={{ fill: "#fff" }} />
                                    )
                                }
                            />
                            <Chip
                                size="small"
                                style={{
                                    height: 30,
                                    width: "100%",
                                    background: data.isKnowledgeCompleted ? "var(--ownGreen)" : "var(--ownRed)",
                                    color: "white",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                }}
                                label={
                                    data.isKnowledgeCompleted
                                        ? getLocalizedString("knowledgeCompleted")
                                        : getLocalizedString("knowledgeIncompleted")
                                }
                                icon={
                                    data.isKnowledgeCompleted ? (
                                        <DoneIcon style={{ fill: "#fff" }} />
                                    ) : (
                                        <CloseIcon style={{ fill: "#fff" }} />
                                    )
                                }
                            />
                        </>
                    )}
                    {isActive && <ProgressBar value={data.progress} />}
                    {Boolean(data.points) && (
                        <Tooltip arrow title={getLocalizedString("trainingMedalTablePoints", { points: data.points })}>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <MedalTableIcon style={{ fill: "var(--mainText)" }} />
                            </div>
                        </Tooltip>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default TrainingSummary;
